import {CommonModule} from '@angular/common';
import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {AvatarModule} from 'primeng/avatar';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UserAvatarService} from './user-avatar.service';
import {getUserInitials, isSystemAvatarUserId} from './user-avatar.util';

@Component({
	selector: 'ngx-user-avatar',
	standalone: true,
	imports: [CommonModule, AvatarModule],
	templateUrl: './user-avatar.component.html',
	styleUrl: './user-avatar.component.scss',
	host: {
		'[class.user-avatar]': 'true',
		'[class.user-avatar--sm]': 'size === "sm"',
		'[class.user-avatar--md]': 'size === "md"',
		'[class.user-avatar--lg]': 'size === "lg"',
	},
})
export class UserAvatarComponent implements OnChanges, OnDestroy {
	@Input() userId: number | string | null = null;
	@Input() firstName?: string | null;
	@Input() lastName?: string | null;
	@Input() size: 'sm' | 'md' | 'lg' = 'md';

	showImage = false;
	imageUrl = '';
	initials = '?';

	private readonly destroy$ = new Subject<void>();
	private readonly refreshDestroy$ = new Subject<void>();
	private readonly avatarRequestDestroy$ = new Subject<void>();
	private watchedUserId: number | null = null;

	constructor(private userAvatarService: UserAvatarService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['userId'] || changes['firstName'] || changes['lastName']) {
			this.bindRefreshListener();
			this.refreshAvatar();
		}
	}

	ngOnDestroy(): void {
		this.avatarRequestDestroy$.next();
		this.avatarRequestDestroy$.complete();
		this.refreshDestroy$.next();
		this.refreshDestroy$.complete();
		this.destroy$.next();
		this.destroy$.complete();
	}

	onImageError(): void {
		this.showImage = false;
	}

	private bindRefreshListener(): void {
		const numericId = this.userId == null || isSystemAvatarUserId(this.userId)
			? null
			: Number(this.userId);

		if (numericId === this.watchedUserId) {
			return;
		}

		this.refreshDestroy$.next();
		this.watchedUserId = numericId;

		if (numericId == null || Number.isNaN(numericId)) {
			return;
		}

		this.userAvatarService.watchUpdates(numericId)
			.pipe(takeUntil(this.refreshDestroy$), takeUntil(this.destroy$))
			.subscribe(() => this.refreshAvatar());
	}

	private refreshAvatar(): void {
		this.initials = getUserInitials(this.firstName, this.lastName);

		if (this.userId == null) {
			this.showImage = false;
			this.imageUrl = '';
			return;
		}

		if (isSystemAvatarUserId(this.userId)) {
			this.showImage = true;
			this.imageUrl = this.userAvatarService.getPicUrl(this.userId);
			return;
		}

		this.avatarRequestDestroy$.next();
		this.userAvatarService.hasCustomPic(this.userId)
			.pipe(takeUntil(this.avatarRequestDestroy$), takeUntil(this.destroy$))
			.subscribe((hasPic) => {
				this.showImage = hasPic;
				this.imageUrl = hasPic ? this.userAvatarService.getPicUrl(this.userId as number | string) : '';
			});
	}
}
